/**
 * Static Asset Operations
 * 
 * Provides operations for static assets, referral codes, referrals, and referral stats
 * with proper offline handling and caching management.
 */

import { BaseOperations } from './shared';
import { STORES } from '../schema';
import type { 
  StaticAssetOffline, 
  ReferralCodeOffline, 
  ReferralOffline, 
  ReferralStatsOffline 
} from '../schema';
import { offlineDB } from '../database';

/**
 * Static Asset Operations
 */
export class StaticAssetOperations extends BaseOperations<StaticAssetOffline> {
  constructor() {
    super(STORES.STATIC_ASSETS);
  }

  /**
   * Get asset by URL
   */
  async getByUrl(url: string): Promise<StaticAssetOffline | null> {
    return await this.get(url);
  }

  /**
   * Get assets by type
   */
  async getByType(assetType: 'logo' | 'image' | 'icon'): Promise<StaticAssetOffline[]> {
    try {
      const allAssets = await this.getAll();
      return allAssets.filter(asset => asset.asset_type === assetType);
    } catch (error) {
      console.error(`Error getting assets by type ${assetType}:`, error);
      return [];
    }
  }

  /**
   * Get expired assets
   */
  async getExpiredAssets(): Promise<StaticAssetOffline[]> {
    const allAssets = await this.getAll();
    const now = new Date();
    return allAssets.filter(asset => new Date(asset.expires_at) < now);
  }

  /**
   * Cache asset from blob
   */
  async cacheAsset(
    url: string,
    blob: Blob,
    assetType: 'logo' | 'image' | 'icon',
    cacheKey: string,
    ttl: number = 7 * 24 * 60 * 60 * 1000 // 7 days default
  ): Promise<StaticAssetOffline> {
    const now = new Date().toISOString();
    const expiresAt = new Date(Date.now() + ttl).toISOString();

    const asset: StaticAssetOffline = {
      id: url,
      asset_type: assetType,
      blob_data: blob,
      mime_type: blob.type,
      file_size: blob.size,
      cached_at: now,
      expires_at: expiresAt,
      last_accessed: now,
      original_url: url,
      cache_key: cacheKey,
      updated_at: now, // Required for BaseOperations compatibility
      _cache_version: 1,
      _last_validated: now
    };

    await offlineDB.put(this.storeName, asset);
    return asset;
  }

  /**
   * Update last accessed time
   */
  async updateLastAccessed(url: string): Promise<void> {
    const asset = await this.get(url);
    if (asset) {
      asset.last_accessed = new Date().toISOString();
      await offlineDB.put(this.storeName, asset);
    }
  }

  /**
   * Remove expired assets
   */
  async removeExpiredAssets(): Promise<number> {
    const expiredAssets = await this.getExpiredAssets();
    let removedCount = 0;

    for (const asset of expiredAssets) {
      await this.delete(asset.id);
      removedCount++;
    }

    return removedCount;
  }
}

/**
 * Referral Code Operations
 */
export class ReferralCodeOperations extends BaseOperations<ReferralCodeOffline> {
  constructor() {
    super(STORES.REFERRAL_CODES);
  }

  /**
   * Get referral code by user ID
   */
  async getByUserId(userId: string): Promise<ReferralCodeOffline | null> {
    const codes = await this.queryByIndex('by_user_id', userId);
    return codes.length > 0 ? codes[0] : null;
  }

  /**
   * Get referral code by code string
   */
  async getByCode(code: string): Promise<ReferralCodeOffline | null> {
    const codes = await this.queryByIndex('by_code', code);
    return codes.length > 0 ? codes[0] : null;
  }

  /**
   * Cache referral code from API response
   */
  async cacheFromAPI(apiCode: Record<string, unknown>): Promise<ReferralCodeOffline> {
    const now = new Date().toISOString();

    const offlineCode: ReferralCodeOffline = {
      id: apiCode.id as string,
      user_id: apiCode.user_id as string,
      code: apiCode.code as string,
      visit_count: apiCode.visit_count as number || 0,
      campaign_visits: apiCode.campaign_visits as Record<string, number> || {},
      created_at: apiCode.created_at as string || now,
      updated_at: apiCode.updated_at as string || now,
      _sync_status: 'synced' as const
    };

    await offlineDB.put(this.storeName, offlineCode);
    return offlineCode;
  }
}

/**
 * Referral Operations
 */
export class ReferralOperations extends BaseOperations<ReferralOffline> {
  constructor() {
    super(STORES.REFERRALS);
  }

  /**
   * Get referrals by referrer ID
   */
  async getByReferrerId(referrerId: string): Promise<ReferralOffline[]> {
    return this.queryByIndex('by_referrer_id', referrerId);
  }

  /**
   * Get referral by referred user ID
   */
  async getByReferredUserId(referredUserId: string): Promise<ReferralOffline | null> {
    const referrals = await this.queryByIndex('by_referred_user_id', referredUserId);
    return referrals.length > 0 ? referrals[0] : null;
  }

  /**
   * Get referrals by status
   */
  async getByStatus(status: string): Promise<ReferralOffline[]> {
    return this.queryByIndex('by_status', status);
  }

  /**
   * Cache referral from API response
   */
  async cacheFromAPI(apiReferral: Record<string, unknown>): Promise<ReferralOffline> {
    const now = new Date().toISOString();

    const offlineReferral: ReferralOffline = {
      id: apiReferral.id as string,
      referrer_id: apiReferral.referrer_id as string,
      referred_user_id: apiReferral.referred_user_id as string,
      source: apiReferral.source as string || 'signup',
      status: apiReferral.status as string || 'signed_up',
      created_at: apiReferral.created_at as string || now,
      updated_at: apiReferral.updated_at as string || now,
      _sync_status: 'synced' as const
    };

    await offlineDB.put(this.storeName, offlineReferral);
    return offlineReferral;
  }

  /**
   * Bulk cache multiple referrals
   */
  async bulkCacheFromAPI(apiReferrals: Record<string, unknown>[]): Promise<ReferralOffline[]> {
    const cachedReferrals: ReferralOffline[] = [];

    for (const apiReferral of apiReferrals) {
      const cached = await this.cacheFromAPI(apiReferral);
      cachedReferrals.push(cached);
    }

    return cachedReferrals;
  }
}

/**
 * Referral Stats Operations
 */
export class ReferralStatsOperations {
  private storeName = STORES.REFERRAL_STATS;

  /**
   * Get cached referral stats for a user
   */
  async getByUserId(userId: string): Promise<ReferralStatsOffline | null> {
    const stats = await offlineDB.queryByIndex<ReferralStatsOffline>(
      this.storeName,
      'by_user_id',
      userId
    );

    // Return the most recent non-expired stats
    const validStats = stats.filter(s => {
      const expiresAt = new Date(s.expires_at);
      return expiresAt > new Date();
    });

    if (validStats.length === 0) return null;

    // Sort by cached_at descending and return the most recent
    validStats.sort((a, b) => new Date(b.cached_at).getTime() - new Date(a.cached_at).getTime());
    return validStats[0];
  }

  /**
   * Cache referral stats for a user
   */
  async cacheStats(
    userId: string,
    stats: {
      total_referrals: string;
      successful_signups: string;
      premium_conversions: string;
      rewards_earned: string;
      progress_to_next_reward: number;
      recent_referrals: Array<{
        id: string;
        created_at: string;
        status: string;
      }>;
    },
    ttl: number = 60 * 60 * 1000 // Default 1 hour
  ): Promise<ReferralStatsOffline> {
    const now = new Date();
    const expiresAt = new Date(now.getTime() + ttl);

    const statsRecord: ReferralStatsOffline = {
      id: crypto.randomUUID(),
      user_id: userId,
      total_referrals: stats.total_referrals,
      successful_signups: stats.successful_signups,
      premium_conversions: stats.premium_conversions,
      rewards_earned: stats.rewards_earned,
      progress_to_next_reward: stats.progress_to_next_reward,
      recent_referrals: stats.recent_referrals,
      cached_at: now.toISOString(),
      expires_at: expiresAt.toISOString(),
      updated_at: now.toISOString(),
      _cache_version: 1,
      _last_validated: now.toISOString()
    };

    await offlineDB.put(this.storeName, statsRecord);
    return statsRecord;
  }

  /**
   * Clear expired stats
   */
  async clearExpiredStats(): Promise<number> {
    const allStats = await offlineDB.getAll<ReferralStatsOffline>(this.storeName);
    const now = new Date();
    let clearedCount = 0;

    for (const stat of allStats) {
      const expiresAt = new Date(stat.expires_at);
      if (expiresAt <= now) {
        await offlineDB.delete(this.storeName, stat.id);
        clearedCount++;
      }
    }

    return clearedCount;
  }
}

// Export singleton instances
export const staticAssetOps = new StaticAssetOperations();
export const referralCodeOps = new ReferralCodeOperations();
export const referralOps = new ReferralOperations();
export const referralStatsOps = new ReferralStatsOperations();
